import reducer from './reducers';
import * as types from './actions';
import request from '../../helpers/request'; // Corrected import path for request.js
import { enqueueNotification } from '../app'; // Assuming this path is correct
import {
  Task,
  FiltersPayload,
  TableColumnPreference,
  SubTask,
} from './types';
import { RootState } from '../../store'; // Import RootState for getState typing
import { User } from '../../types';

export default reducer;

// Action Creators
// Updated signature to accept an object with a 'filters' key
export const getDashboardTasks = (params: { filters: FiltersPayload, page:number, limit?:number }) => {
  console.log({page: params.page, limit: params.limit}); // Log page and limit for debugging
  return async (dispatch: any) => {
    dispatch({ type: types.GET_DASHBOARD_TASKS_LOADING });
    try {
      // Extract filtersPayload from params and stringify it for the API call
      const { data } = await request.get(`/api/tasks/v1`, { filters: JSON.stringify(params.filters), page:params.page, limit: params.limit }, dispatch);
      console.log({data})
      if (data.success) {
        dispatch({
          type: types.GET_DASHBOARD_TASKS_SUCCESS,
          payload: {
            tasks: data.data as Task[],
            filters: params.filters,
            page: params.page,
          },
        });
        return Promise.resolve(data);
      } else {
        const message = data.message || 'Failed to load dashboard tasks.';
        dispatch({
          type: types.GET_DASHBOARD_TASKS_FAILURE,
          payload: message,
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err: any) {
      const message = 'Something went wrong while loading dashboard tasks, please try again.';
      dispatch({
        type: types.GET_DASHBOARD_TASKS_FAILURE,
        payload: message,
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};

interface AddTaskPayload {
  title: string;
  description?: string;
  due_date?: string;
  status: string;
  assignees: string[]; // Array of user IDs
  priority?: string;
  resource_type: string;
  resource_name?: string; // Optional, if you want to include resource name
  resource_id: string;
  rrule_string?: string;
  attachmentIds?: string[];
  sub_tasks?: SubTask[];
  parent_id?: string | null; // Optional, if the task is a subtask
}

export const addDashboardTask = (taskData: AddTaskPayload, ) => {
  return async (dispatch: any) => {
    dispatch({ type: types.ADD_DASHBOARD_TASK_LOADING });
    try {
      const { data } = await request.post(`/api/tasks/v1`, taskData, dispatch);
      if (data.success) {
        dispatch({
          type: types.ADD_DASHBOARD_TASK_SUCCESS,
          payload: {data:data.data as Task, resource_name: taskData.resource_name}, // Assuming data.data is the newly created task object
        });
        return Promise.resolve(data);
      } else {
        const message = data.message || 'Failed to add task.';
        dispatch({
          type: types.ADD_DASHBOARD_TASK_FAILURE,
          payload: message, // No taskId needed for rollback as no optimistic update
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err: any) {
      const message = 'Something went wrong while adding task, please try again.';
      dispatch({
        type: types.ADD_DASHBOARD_TASK_FAILURE,
        payload: message, // No taskId needed for rollback as no optimistic update
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};

interface UpdateTaskPayload {
  tasks_to_update: string[]; // Array of task IDs
  attributesToUpdate: {
    title?: string;
    priority?: string| null;
    status?: string;
    position_id?: number;
    assignees?: { id: string; user: User }[] | string[]; // Array of full user objects for optimistic update
    repeating_rule?: string;
    due_date?: string;
    // Add other updatable fields here
  };
}

export const updateDashboardTask = (updateData: UpdateTaskPayload, parent_id?: string | null) => {
  return async (dispatch: any, getState: () => RootState) => {
  
    const taskId = updateData.tasks_to_update[0];
    const originalTask: Task | undefined = parent_id
      ? getState().taskDashboard.dashboardTasksById[parent_id]?.sub_tasks?.find(
          sTask => sTask.id === taskId
        )
      : getState().taskDashboard.dashboardTasksById[taskId];

    // Optimistically update the state immediately
    dispatch({
      type: types.UPDATE_DASHBOARD_TASK_LOADING,
      payload: {
        taskId,
        originalTask, // Store original for rollback
        updatedAttributes: updateData.attributesToUpdate, // Pass attributes for optimistic update
      },
    });

    // Prepare data for API call: convert assignees to array of IDs if present
    const apiUpdateData = { ...updateData };
    if (apiUpdateData.attributesToUpdate.assignees) {
      apiUpdateData.attributesToUpdate.assignees = apiUpdateData.attributesToUpdate.assignees.map(
        assignee => (typeof assignee === 'object' ? assignee.id : assignee)
      );
    }
   

    try {
      const { data } = await request.put(`/api/tasks/v1`, apiUpdateData, dispatch);

      if (data.success) {
        // If a subtask is updated to 'in_progress' and its parent is 'todo', update parent
        if (
          originalTask && // Ensure originalTask is defined
          originalTask.parent_id &&
          updateData.attributesToUpdate.status === 'in_progress'
        ) {
          const mainTask = getState().taskDashboard.dashboardTasksById[
            originalTask.parent_id
          ];
          if (mainTask && mainTask.status === 'todo') {
            // Directly update the Redux state for the main task
            dispatch({
              type: types.UPDATE_DASHBOARD_TASK_LOADING,
              payload: {
                taskId: mainTask.id,
                originalTask: mainTask, // Pass the current state of the main task as original
                updatedAttributes: { status: 'in_progress' },
              },
            });
          }
        }

        // New logic for subtask status change to 'complete'
        if (
          originalTask &&
          originalTask.parent_id &&
          updateData.attributesToUpdate.status === 'complete'
        ) {
          const mainTask = getState().taskDashboard.dashboardTasksById[
            originalTask.parent_id
          ];
          if (mainTask) {
            // Get the latest subtasks from the state, applying the current update optimistically
            const updatedSubtasks = mainTask.sub_tasks.map(subtask =>
              subtask.id === taskId
                ? { ...subtask, status: updateData.attributesToUpdate.status }
                : subtask
            );

            // Check if all subtasks (including the current one) are complete
            const allSubtasksComplete = updatedSubtasks.every(
              subtask => subtask.status === 'complete'
            );

            if (allSubtasksComplete && mainTask.status !== 'complete') {
              // If all subtasks are complete and parent is not already complete, update parent via API
              dispatch(
                updateDashboardTask(
                  {
                    tasks_to_update: [mainTask.id],
                    attributesToUpdate: { status: 'complete' },
                  },
                  null // Main task has no parent_id
                )
              );
            }
          }
        }

        // New logic: If main task status is changed to complete, change all subtask statuses to complete
        if (
          originalTask &&
          !originalTask.parent_id && // It's a main task
          updateData.attributesToUpdate.status === 'complete' &&
          originalTask.sub_tasks &&
          originalTask.sub_tasks.length > 0
        ) {
          originalTask.sub_tasks.forEach(subtask => {
            if (subtask.status !== 'complete') {
              dispatch({
                type: types.UPDATE_DASHBOARD_TASK_LOADING,
                payload: {
                  taskId: subtask.id,
                  originalTask: subtask,
                  updatedAttributes: { status: 'complete' },
                },
              });
            }
          });
        }

      
        // New logic for subtask due_date update affecting parent task
        if (
          originalTask &&
          originalTask.parent_id && // It's a subtask
          updateData.attributesToUpdate.due_date
        ) {
          const mainTask = getState().taskDashboard.dashboardTasksById[
            originalTask.parent_id
          ];
          const newSubtaskDueDate = new Date(updateData.attributesToUpdate.due_date);

          if (mainTask) {
            if (!mainTask.due_date) {
              // Condition 1: Main task has no due date, update it to subtask's due date
              dispatch(
                updateDashboardTask(
                  {
                    tasks_to_update: [mainTask.id],
                    attributesToUpdate: { due_date: newSubtaskDueDate.toISOString() },
                  },
                  null // Main task has no parent_id
                )
              );
            } else {
              // Condition 2: Main task has a due date, update if subtask's due date is earlier
              const mainTaskDueDate = new Date(mainTask.due_date);
              if (newSubtaskDueDate > mainTaskDueDate) {
                dispatch(
                  updateDashboardTask(
                    {
                      tasks_to_update: [mainTask.id],
                      attributesToUpdate: { due_date: newSubtaskDueDate.toISOString() },
                    },
                    null // Main task has no parent_id
                  )
                );
              }
            }
          }
        }

        dispatch({
          type: types.UPDATE_DASHBOARD_TASK_SUCCESS,
          // No payload needed, state already updated optimistically
        });
        return Promise.resolve(data);
      } else {
        const message = data.message || 'Failed to update task.';
        dispatch({
          type: types.UPDATE_DASHBOARD_TASK_FAILURE,
          payload: { message, taskId, originalTask }, // Pass taskId for optimistic rollback
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err: any) {
      const message = 'Something went wrong while updating task, please try again.';
      dispatch({
        type: types.UPDATE_DASHBOARD_TASK_FAILURE,
        payload: { message, taskId, originalTask }, // Pass taskId for optimistic rollback
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};

export const TASK_DELETE_OPTIONS = {
  'individual': { label: 'Only this task', key: 'individual' },
  'upcoming': {
    label: 'This task & following tasks',
    key: 'upcoming',
  },
  'all': { label: 'All Tasks', key: 'all' },
};

export const deleteDashboardTask = (taskId: string, deleteOption: keyof typeof TASK_DELETE_OPTIONS, parent_id?: string|null) => {
  return async (dispatch: any, getState: () => RootState) => {
    const originalTask = getState().taskDashboard.dashboardTasksById[taskId]; // Get current task from state

    // Optimistically delete the task from the state immediately
    dispatch({ type: types.DELETE_DASHBOARD_TASK_LOADING, payload: { taskId, originalTask, parent_id } }); // Pass taskId, original task, and parent_id for optimistic rollback

    try {
      const { data } = await request.del(`/api/tasks/v1/${taskId}?deleteOption=${deleteOption}`, {}, dispatch); // Corrected to request.del
      if (data.success) {
        dispatch({
          type: types.DELETE_DASHBOARD_TASK_SUCCESS,
          // No payload needed, state already updated optimistically
        });
        return Promise.resolve(data);
      } else {
        const message = data.message || 'Failed to delete task.';
        dispatch({
          type: types.DELETE_DASHBOARD_TASK_FAILURE,
          payload: { message, taskId,parent_id }, // Pass taskId for optimistic rollback
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err: any) {
      const message = 'Something went wrong while deleting task, please try again.';
      dispatch({
        type: types.DELETE_DASHBOARD_TASK_FAILURE,
        payload: { message, taskId, parent_id }, // Pass taskId for optimistic rollback
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};

export const getDashboardTaskById = (taskId: string) => {
  return async (dispatch: any) => {
    dispatch({ type: types.GET_DASHBOARD_TASK_BY_ID_LOADING });
    try {
      const { data } = await request.get(`/api/tasks/v1/${taskId}`, {}, dispatch);
      if (data.success) {
        dispatch({
          type: types.GET_DASHBOARD_TASK_BY_ID_SUCCESS,
          payload: data.data as Task,
        });
        return Promise.resolve(data);
      } else {
        const message = data.message || 'Failed to load task by ID.';
        dispatch({
          type: types.GET_DASHBOARD_TASK_BY_ID_FAILURE,
          payload: message,
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err: any) {
      const message = 'Something went wrong while loading task by ID, please try again.';
      dispatch({
        type: types.GET_DASHBOARD_TASK_BY_ID_FAILURE,
        payload: message,
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};

export const loadTableColumnPreferences = () => {
  return async (dispatch: any) => {
    dispatch({ type: types.LOAD_TABLE_COLUMN_PREFERENCES_LOADING });
    try {
      const { data } = await request.get(`/api/tasks/v1/task_dashboard_table_columns`, {}, dispatch);
      if (data.success) {
        dispatch({
          type: types.LOAD_TABLE_COLUMN_PREFERENCES_SUCCESS,
          payload: data.data as TableColumnPreference[],
        });
        return Promise.resolve(data);
      } else {
        const message = data.message || 'Failed to load column preferences.';
        dispatch({
          type: types.LOAD_TABLE_COLUMN_PREFERENCES_FAILURE,
          payload: message,
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err: any) {
      const message = 'Something went wrong while loading column preferences, please try again.';
      dispatch({
        type: types.LOAD_TABLE_COLUMN_PREFERENCES_FAILURE,
        payload: message,
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};

export const updateTableColumnPreferences = (preferences: TableColumnPreference[]) => {
  return async (dispatch: any, getState: () => RootState) => {
    const originalPreferences = getState().taskDashboard.tableColumnPreferences; // Get current preferences for rollback

    // Optimistically update the state immediately
    dispatch({
      type: types.UPDATE_TABLE_COLUMN_PREFERENCES_LOADING,
      payload: preferences, // New preferences to apply
    });

    try {
      const { data } = await request.put(`/api/tasks/v1/task_dashboard_table_columns`, { columns: preferences }, dispatch);
      if (data.success) {
        dispatch({
          type: types.UPDATE_TABLE_COLUMN_PREFERENCES_SUCCESS,
          // No payload needed, state already updated optimistically
        });
        return Promise.resolve(data);
      } else {
        const message = data.message || 'Failed to update column preferences.';
        dispatch({
          type: types.UPDATE_TABLE_COLUMN_PREFERENCES_FAILURE,
          payload: { message, originalPreferences }, // Pass original for optimistic rollback
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err: any) {
      const message = 'Something went wrong while updating column preferences, please try again.';
      dispatch({
        type: types.UPDATE_TABLE_COLUMN_PREFERENCES_FAILURE,
        payload: { message, originalPreferences }, // Pass original for optimistic rollback
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};
